import { useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import dropDownIcon from 'assets/dropDownIcon.svg';
import ReviewsMagnifyIcon from 'assets/ReviewsMagnifyIcon.svg';

import HelpButton from 'Components/HelpButton';

import DashboardHeader from 'Pages/TeamDashboard/Home/components/DashboardHeader';
import Sidebar from 'Pages/TeamDashboard/Home/components/Sidebar';

import {
  cancelInvitation,
  getMembers,
  MEMBER_ROLES,
  removeMember,
  updateMemberRole,
} from 'services/membersService';

import { errorReactToastify, infoReactToastify, successReactToastify } from 'utlis/toasts';

import InviteModal from './InviteModal';
import MemberRow from './MemberRow';

const MEMBERS_PAGE_SIZE = 20;

const ROLE_FILTER_OPTIONS = [
  { value: MEMBER_ROLES.ALL, label: 'All' },
  { value: MEMBER_ROLES.EDITOR, label: 'Editor' },
  { value: MEMBER_ROLES.VIEW_ONLY, label: 'View only' },
];

const Members = () => {
  const { selectedWorkspace, isOwner, isEditor, isLoadingWorkspaces } = useWorkspace();
  const currentUserEmail = useSelector(state => state.userStore?.email);

  const [members, setMembers] = useState([]);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(MEMBER_ROLES.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const searchTimeoutRef = useRef(null);
  const prevWorkspaceUuidRef = useRef(selectedWorkspace?.uuid);

  const fetchMembers = async ({
    search = searchTerm,
    role = roleFilter,
    page = currentPage,
  } = {}) => {
    if (isLoadingWorkspaces || !selectedWorkspace?.uuid) {
      return;
    }

    setIsLoading(true);

    try {
      const data = await getMembers({
        workspaceUuid: selectedWorkspace.uuid,
        search,
        role,
        page,
        pageSize: MEMBERS_PAGE_SIZE,
      });

      setMembers(data.results);
      setTotalCount(data.count);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = e => {
    const value = e.target.value;

    setSearchTerm(value);
    setCurrentPage(1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchMembers({ search: value, page: 1 });
    }, 500);
  };

  const handleRoleFilterChange = e => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handleRoleChange = async (member, newRole) => {
    if (member.role === newRole) {
      const roleLabel = newRole === MEMBER_ROLES.EDITOR ? 'Editor' : 'View only';

      infoReactToastify(`This member already has the ${roleLabel} role.`);

      return;
    }

    const previousRole = member.role;

    // Optimistic update
    setMembers(prev => prev.map(m => m.uuid === member.uuid ? { ...m, role: newRole } : m));

    try {
      await updateMemberRole({
        workspaceUuid: selectedWorkspace.uuid,
        memberUuid: member.uuid,
        role: newRole,
      });
      successReactToastify('Member role updated');
    }
    catch {
      // Revert on failure
      setMembers(prev => prev.map(m => m.uuid === member.uuid ? { ...m, role: previousRole } : m));
      errorReactToastify('Failed to update member role');
    }
  };

  const isPendingInvite = useMemo(
    () => memberToRemove?.role === MEMBER_ROLES.PENDING || memberToRemove?.status === MEMBER_ROLES.PENDING,
    [memberToRemove]
  );

  const handleConfirmRemove = async () => {
    if (!memberToRemove || isRemoving) {
      return;
    }

    setIsRemoving(true);

    try {
      if (isPendingInvite) {
        await cancelInvitation({
          workspaceUuid: selectedWorkspace.uuid,
          invitationUuid: memberToRemove.uuid,
        });
        successReactToastify('Invite cancelled');
      }
      else {
        await removeMember({
          workspaceUuid: selectedWorkspace.uuid,
          memberUuid: memberToRemove.uuid,
        });
        successReactToastify('Member removed');
      }

      setMemberToRemove(null);
      fetchMembers();
    }
    catch (err) {
      const detail = err?.response?.data?.detail;

      errorReactToastify(detail || (
        isPendingInvite ? 'Failed to cancel invite' : 'Failed to remove member'
      ));
    }
    finally {
      setIsRemoving(false);
    }
  };

  useEffect(() => {
    if (isLoadingWorkspaces) {
      return;
    }

    if (!selectedWorkspace?.uuid) {
      setIsLoading(false);

      return;
    }

    const workspaceChanged = prevWorkspaceUuidRef.current !== selectedWorkspace?.uuid;

    prevWorkspaceUuidRef.current = selectedWorkspace?.uuid;

    if (workspaceChanged) {
      setSearchTerm('');
      setRoleFilter(MEMBER_ROLES.ALL);
      setCurrentPage(1);
      fetchMembers({ search: '', role: MEMBER_ROLES.ALL, page: 1 });
    }
    else {
      fetchMembers();
    }
  }, [selectedWorkspace?.uuid, currentPage, roleFilter, isLoadingWorkspaces]);

  useEffect(() => () => {
    clearTimeout(searchTimeoutRef.current);
  }, []);

  useEffect(() => {
    const handleClickOutside = e => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target)) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalPages = Math.ceil(totalCount / MEMBERS_PAGE_SIZE);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * MEMBERS_PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * MEMBERS_PAGE_SIZE, totalCount);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) {
        pages.push(i);
      }

      pages.push('...');
      pages.push(totalPages);
    }
    else if (currentPage >= totalPages - 2) {
      pages.push(1);
      pages.push('...');

      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    }
    else {
      pages.push(1);
      pages.push('...');

      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }

      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }, [totalPages, currentPage]);

  const canInviteMembers = isOwner || isEditor;

  const inviteMembersButton = canInviteMembers
    ? (
      <button
        onClick={() => setIsInviteModalOpen(true)}
        className="flex items-center justify-center px-3 py-2 font-inter text-sm font-medium
        rounded-lg shadow-shadow-xs transition-colors bg-primary-500 text-white hover:bg-primary-600"
      >
        Invite members
      </button>
    )
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex w-full">
      <Sidebar />
      <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
        <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm relative">
          <DashboardHeader title="Members" actionButton={inviteMembersButton} showProductLibrary={false} />

          <div className="flex flex-1 flex-col overflow-auto px-6 py-6">
            {/* Search + Role filter */}
            <div className="flex items-center justify-between mb-6">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or email"
                  autoComplete="off"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm font-inter
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-80"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <img src={ReviewsMagnifyIcon} width={16} height={16} alt="Search" />
                </div>
              </div>

              {/* Role filter */}
              <div className="relative" ref={roleDropdownRef}>
                <button
                  type="button"
                  className="flex items-center gap-8 bg-white border border-gray-300 rounded-md px-3 py-2"
                  onClick={() => setIsRoleDropdownOpen(prev => !prev)}
                >
                  <span className="text-sm font-inter text-figma-gray-500">
                    Role:
                  </span>
                  <span className="text-sm font-inter text-gray-700">
                    {ROLE_FILTER_OPTIONS.find(o => o.value === roleFilter)?.label}
                  </span>
                  <img src={dropDownIcon} alt="" className="ml-1" />
                </button>

                {isRoleDropdownOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 z-10 bg-white border border-gray-200
                    rounded-md shadow-md min-w-full overflow-hidden"
                  >
                    {ROLE_FILTER_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm font-inter hover:bg-gray-50',
                          {
                            'text-primary-500 font-medium': option.value === roleFilter,
                            'text-gray-700': option.value !== roleFilter,
                          }
                        )}
                        onClick={() => {
                          handleRoleFilterChange({ target: { value: option.value } });
                          setIsRoleDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isLoading
              ? (
                <div className="flex flex-1 justify-center items-center">
                  <ImpulseSpinner frontColor="#28BDAE" />
                </div>
              )
              : (
                <div className="flex flex-col flex-1 overflow-auto">
                  {/* Table header */}
                  <div className="flex items-center border-b border-gray-200 py-3 px-2 gap-16">
                    <div className="flex-[4]">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Name
                      </span>
                    </div>
                    <div className="flex-[2]">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Permissions
                      </span>
                    </div>
                    <div className="flex-[2]">
                      <span className="font-inter text-sm font-medium text-figma-gray-500 leading-5">
                        Last active
                      </span>
                    </div>
                    <div className="flex-[2]" >
                      Audits
                    </div>
                    {/* Spacer matching actions cell width */}
                    <div className="flex-shrink-0 w-9" />
                  </div>

                  {/* Table rows */}
                  {members.length === 0
                    ? (
                      <div className="flex flex-1 justify-center items-center">
                        <span className="font-inter text-sm text-figma-gray-500">
                          No members found
                        </span>
                      </div>
                    )
                    : members.map(member => (
                      <MemberRow
                        key={member.email}
                        member={member}
                        isOwner={isOwner}
                        currentUserEmail={currentUserEmail}
                        onRoleChange={handleRoleChange}
                        onRemove={setMemberToRemove}
                      />
                    ))}

                  {/* Pagination */}
                  {totalCount > 0 && (
                    <div
                      className="flex items-center justify-between px-0 pt-[13px]
                      pr-16 border-t border-gray-200 mt-auto"
                    >
                      <div className="font-inter text-sm text-figma-gray-600">
                        Showing
                        {' '}
                        <span className="font-semibold">
                          {totalPages > 1 ? `${startItem}–${endItem}` : totalCount}
                        </span>
                        {' '}
                        of
                        {' '}
                        <span className="font-semibold">
                          {totalCount}
                        </span>
                      </div>

                      <div className="flex items-center border border-[#CBCFD6] rounded-md">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-figma-gray-600
                              hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {pageNumbers.map((page, index) => (
                          <button
                            key={`${index}-${page}`}
                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                            disabled={page === '...'}
                            className={cn(
                              'flex px-3 py-[0.375rem] flex-col justify-center items-center text-sm font-medium',
                              {
                                'bg-primary-400 text-white': page === currentPage,
                                'bg-white text-figma-gray-400 cursor-default': page === '...',
                                'bg-white text-figma-gray-600 hover:text-figma-gray-900':
                                    page !== currentPage && page !== '...',
                              }
                            )}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-figma-gray-600
                              hover:text-figma-gray-900 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>

          <HelpButton />
        </div>
      </div>

      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInviteSuccess={fetchMembers}
        workspaceUuid={selectedWorkspace?.uuid}
      />

      {/* Remove member / cancel invite confirmation modal */}
      {memberToRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur"
          onClick={() => !isRemoving && setMemberToRemove(null)}
        >
          <div
            className="bg-white border border-gray-200 rounded-2xl w-full max-w-[520px]
              flex flex-col"
            style={{ boxShadow: '0px 6px 24px -2px rgba(0,0,0,0.08), 0px 3px 6px rgba(0,0,0,0.04)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-16 border-b border-gray-200">
              <h2 className="font-inter font-semibold text-base text-figma-gray-900">
                {isPendingInvite
                  ? 'Cancel invite for'
                  : 'Remove'}
                {' '}
                {memberToRemove.full_name || memberToRemove.email}
                ?
              </h2>
              <button
                type="button"
                className="text-[#8F97A5] hover:text-figma-gray-700 flex-shrink-0"
                onClick={() => !isRemoving && setMemberToRemove(null)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 5L5 15M5 5l10 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <p className="font-inter font-normal text-base leading-6 text-figma-gray-900">
                {isPendingInvite
                  ? (
                    <>
                      {memberToRemove.email}
                      {' '}
                      will no longer be able to join
                      {' "'}
                      {selectedWorkspace?.name}
                      {'"'}
                      {' '}
                      with this invite. You can invite them again later.
                    </>
                  )
                  : (
                    <>
                      {memberToRemove.full_name || memberToRemove.email}
                      {' '}
                      will no longer have access to
                      {' "'}
                      {selectedWorkspace?.name}
                      {'"'}
                      . Any audits created by them will remain available to this team.
                    </>
                  )}
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-16 border-t border-gray-200">
              <button
                type="button"
                disabled={isRemoving}
                className="px-5 py-[10px] bg-[#EF2A35] text-white font-inter text-sm font-medium
                  rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50
                  disabled:cursor-not-allowed"
                onClick={handleConfirmRemove}
              >
                {isPendingInvite
                  ? (isRemoving ? 'Cancelling…' : 'Cancel invite')
                  : (isRemoving ? 'Removing…' : 'Remove member')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
